// ---------------------------------------------------------------------------
// MonthSelectorV3 — barra superior horizontal de seleção de meses.
// ---------------------------------------------------------------------------
// Equivalente visual da variação "F2" (segmented control) validada em
// `sandbox/inputs_v3_overview_and_monthpicker.html`. Substitui o V2
// `CustomTabs` / `CustomTab` baseado em Material-UI Tabs.
//
// Premissas do design:
// - Tabs horizontais agrupadas por ano, com um cap-label pequeno acima.
// - Label exibe só o mês (3 letras) — o ano já está no cap.
// - Trilho cinza claro (segmented control) com cápsula branca destacada
//   para o mês selecionado (sombra leve cinza) — fidelidade ao HTML.
// - Micro-barra de 8px abaixo de cada mês codifica o estado agregado do
//   mês (waiting/action/done/stale). O mês atual fica intencionalmente
//   sem bolinha (convenção do protótipo).
// - Fade visual (tons mais claros) para meses não selecionados, sem
//   alterar a cor do status.
//
// Integração:
// - Recebe `monthlyStatus` (array V2) e mapeia cada status (v2) para o
//   vocabulário V3 (waiting/action/done/stale) internamente. Futuramente
//   o backend V3 expõe o vocabulário diretamente e esse mapa some.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import cx from "classnames";

import {
  grayColor,
  warningColor,
} from "../../../assets/jss/material-dashboard-pro-react";
import StagePips from "./StagePips";
import {
  classifyMonthAggregate,
  monthStateLabel,
} from "../../../services/inputsV3StateRules";

// Abreviações de meses usadas no dicionário common.months do projeto.
const MONTH_ABBR = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

// Primeiras 3 letras do nome do mês em PT-BR (fallback / tooltip).
const MONTHS_PT_SHORT = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

// Vocabulario unificado vive em ``services/inputsV3StateRules.js``
// (PA-006). Aqui apenas reaproveitamos ``classifyMonthAggregate`` —
// foi historicamente uma ``statusV2toV3`` local com a mesma logica.

// Cores e animacoes do tracinho de status do mes vivem em
// ``StagePips/StagePips.js`` (paleta unificada com os tracinhos do
// InputBoxV3). Aqui mantemos apenas o que precisamos para o tooltip
// (rotulo do estado).

const useStyles = makeStyles(() => ({
  wrap: {
    padding: "2px 8px 6px",
    background: "#ffffff",
    border: `1px solid ${grayColor[5]}`,
    borderRadius: "6px",
    // Ocupa todo o espaco horizontal disponivel (antes
    // ``inline-block`` fechava a borda em volta do conteudo e deixava
    // vazio a direita). ``block`` + ``width: 100%`` estica a barra no
    // container pai ate o limite, mantendo o overflowX para garantir
    // scroll horizontal quando o numero de meses extrapolar o espaco.
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    overflowX: "auto",
    // Scrollbar discreta — mesmo padrão do overlay do V3.
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    "&:hover": { scrollbarColor: `${grayColor[6]} transparent` },
    "&::-webkit-scrollbar": { height: "6px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      background: "transparent",
      borderRadius: "3px",
      transition: "background 0.2s ease",
    },
    "&:hover::-webkit-scrollbar-thumb": { background: grayColor[6] },
  },
  row: {
    display: "inline-flex",
    alignItems: "flex-start",
    padding: "2px 2px 4px",
  },
  yearBlock: {
    display: "inline-flex",
    flexDirection: "column",
    "& + &": { marginLeft: "8px" },
  },
  yearCap: {
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    color: grayColor[0],
    fontWeight: 300,
    padding: "0 6px",
  },
  track: {
    display: "inline-flex",
    padding: "3px 0",
    background: grayColor[13], // #f5f5f5
    border: `1px solid ${grayColor[8]}`, // #eeeeee
    borderRadius: "7px",
  },
  yearTabs: { display: "inline-flex" },
  tab: {
    flexShrink: 0,
    padding: "4px 8px 10px",
    margin: "0 3px",
    fontSize: "12px",
    fontWeight: 400,
    color: grayColor[1],
    cursor: "pointer",
    position: "relative",
    whiteSpace: "nowrap",
    borderRadius: "5px",
    transition:
      "background 160ms ease, box-shadow 160ms ease, color 160ms ease",
    background: "transparent",
    border: "none",
    fontFamily: "inherit",
    outline: "none",
    "&:hover": { color: grayColor[2] },
    "&:focus-visible": {
      boxShadow: `0 0 0 2px ${grayColor[5]}`,
    },
  },
  tabActive: {
    background: "#ffffff",
    color: grayColor[2],
    fontWeight: 500,
    boxShadow: "0 0 4px #00000033",
    "&:hover": { color: grayColor[2] },
  },
  // Wrapper que posiciona o ``StagePips variant="month"`` (1 tracinho)
  // centralizado abaixo do nome do mes. O styling do tracinho em si
  // (cores, animacoes, tamanho) vive em ``StagePips/StagePips.js``.
  tabBarWrap: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    bottom: "3px",
    display: "inline-flex",
    pointerEvents: "none",
  },
  // Marcador de "mes tem pendencias do usuario" (leva 57-G: cobre
  // decisoes pendentes + inputs nao enviados — alimentado pelo
  // endpoint ``/api/v3/pending-summary/``). Pequeno ponto laranja no
  // canto superior direito do tab. Distinto da barra de estado (barra
  // = status agregado do mes; ponto = o usuario tem algo a resolver).
  tabPendingDecisionDot: {
    position: "absolute",
    top: "3px",
    right: "3px",
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    background: warningColor[0],
  },
}));

/**
 * Agrupa um array de meses (V2-shape: {year, month, status}) por ano,
 * preservando a ordem de entrada (assume cronológica crescente).
 */
function groupByYear(monthlyStatus) {
  const out = [];
  monthlyStatus.forEach((m, i) => {
    const last = out[out.length - 1];
    if (last && last.year === m.year) last.items.push({ month: m, idx: i });
    else out.push({ year: m.year, items: [{ month: m, idx: i }] });
  });
  return out;
}

function MonthSelectorV3({
  monthlyStatus,
  selectedMonth,
  onSelectMonth,
  pendingDecisionsByMonth,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const selectedRef = useRef(null);

  const grouped = useMemo(() => groupByYear(monthlyStatus), [monthlyStatus]);

  // Ao montar (ou quando o mês selecionado muda), garante que o tab
  // selecionado esteja visível — por padrão o "mês atual" é o último,
  // então inicia à direita; depois preserva a decisão do usuário.
  useEffect(() => {
    if (!selectedRef.current || !scrollRef.current) return;
    const wrap = scrollRef.current;
    const tab = selectedRef.current;
    const tabLeft = tab.offsetLeft;
    const tabRight = tabLeft + tab.offsetWidth;
    const viewLeft = wrap.scrollLeft;
    const viewRight = viewLeft + wrap.clientWidth;
    if (tabLeft < viewLeft || tabRight > viewRight) {
      wrap.scrollLeft = tabLeft - wrap.clientWidth / 2 + tab.offsetWidth / 2;
    }
  }, [selectedMonth, monthlyStatus.length]);

  if (!monthlyStatus || monthlyStatus.length === 0) return null;

  return (
    <div className={classes.wrap} ref={scrollRef}>
      <div className={classes.row}>
        {grouped.map((y) => (
          <div key={y.year} className={classes.yearBlock}>
            <div className={classes.yearCap}>{y.year}</div>
            <div className={classes.track}>
              <div className={classes.yearTabs}>
                {y.items.map(({ month, idx }) => {
                  const isActive = idx === selectedMonth;
                  const state = classifyMonthAggregate(month.status);
                  const label =
                    t(`common.months.${MONTH_ABBR[month.month - 1]}`) ||
                    MONTHS_PT_SHORT[month.month - 1];
                  // 3 letras, primeira maiúscula.
                  const short = label.slice(0, 3);
                  const isLast = idx === monthlyStatus.length - 1;
                  const monthKey = `${month.year}-${String(month.month).padStart(2, "0")}`;
                  const hasUserPending = !!(
                    pendingDecisionsByMonth &&
                    pendingDecisionsByMonth.has &&
                    pendingDecisionsByMonth.has(monthKey)
                  );
                  const showPendingDecisionPip =
                    Boolean(month.has_pending_input_decision) || hasUserPending;
                  const pipState = showPendingDecisionPip
                    ? "pending_decision"
                    : state;
                  return (
                    <Tooltip
                      key={idx}
                      title={
                        <span>
                          <strong>{`${label.charAt(0).toUpperCase() + label.slice(1)} · ${month.year}`}</strong>
                          <br />
                          {monthStateLabel(state, month.reason || null, t)}
                          {hasUserPending && (
                            <>
                              <br />
                              <span style={{ color: warningColor[0] }}>
                                {t(
                                  "modeling_inputs_v3.month_selector.pending_in_this_month",
                                  "Você tem pendências neste mês",
                                )}
                              </span>
                            </>
                          )}
                        </span>
                      }
                      placement="top"
                      enterDelay={300}
                      leaveDelay={0}
                    >
                      <button
                        type="button"
                        ref={isActive ? selectedRef : null}
                        className={cx(classes.tab, {
                          [classes.tabActive]: isActive,
                        })}
                        onClick={() => onSelectMonth(idx)}
                      >
                        {short.charAt(0).toUpperCase() + short.slice(1)}
                        {/* A-003: a barra de estado e' renderizada
                            INCLUSIVE no mes atual, desde o primeiro
                            paint. */}
                        <span className={classes.tabBarWrap}>
                          <StagePips variant="month" states={[pipState]} />
                        </span>
                        {/* eslint-disable-next-line no-unused-expressions */}
                        {isLast && null}
                        {hasUserPending && (
                          <span
                            className={classes.tabPendingDecisionDot}
                            aria-label={t(
                              "modeling_inputs_v3.month_selector.pending_in_this_month",
                              "Você tem pendências neste mês",
                            )}
                          />
                        )}
                      </button>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

MonthSelectorV3.propTypes = {
  monthlyStatus: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.number.isRequired,
      month: PropTypes.number.isRequired,
      status: PropTypes.string,
      has_pending_input_decision: PropTypes.bool,
    }),
  ).isRequired,
  selectedMonth: PropTypes.number,
  onSelectMonth: PropTypes.func.isRequired,
  // ``Set<string>`` com as chaves ``"YYYY-MM"`` dos meses que tem
  // QUALQUER pendencia do usuario atual — decisoes pendentes OU
  // inputs nao enviados (semi-fixos/fixos com sucesso anterior
  // excluidos). Alimentado pelo consumidor (hoje, ``InputsV3``) via
  // ``GET /api/v3/pending-summary/`` (leva 57-G), que ja filtra por
  // responsabilidade. Nome historico mantido para compatibilidade.
  pendingDecisionsByMonth: PropTypes.object,
};

export default MonthSelectorV3;
